import type {
  Booking,
  CheckinStatus,
  CheckoutStatus,
  PreCheckStatus,
} from "@/types/booking";
import { GuestStatus } from "@/types/guest";
import { CHECKED_OUT_GUEST_STATUS } from "../../utils/reservationGuestStatusFilters";

export type GuestStatusInput =
  | Booking
  | {
      preCheckinStatus?: PreCheckStatus;
      checkinStatus?: CheckinStatus;
      preCheckoutStatus?: PreCheckStatus;
      checkoutStatus?: CheckoutStatus;
    };

/**
 * Determines the guest status based on booking status fields
 * @param booking - The booking object or any object with status fields
 * @returns GuestStatus enum value
 */
export function determineGuestStatus(
  booking: GuestStatusInput | null | undefined,
): GuestStatus | null {
  if (!booking) {
    return null;
  }

  const { preCheckinStatus, checkinStatus, preCheckoutStatus, checkoutStatus } =
    booking;

  if (preCheckinStatus !== "DONE" && checkinStatus === "NOT_DONE") {
    return GuestStatus.PRE_CHECKIN;
  }

  if (
    checkinStatus === "DONE" &&
    preCheckoutStatus === "SENT" &&
    checkoutStatus === "NOT_DONE"
  ) {
    return GuestStatus.PRE_CHECKOUT;
  }

  if (preCheckinStatus === "DONE" && checkinStatus === "NOT_DONE") {
    return GuestStatus.BEFORE_CHECK_IN;
  }

  if (preCheckoutStatus === "DONE" && checkoutStatus === "NOT_DONE") {
    return GuestStatus.BEFORE_CHECK_OUT;
  }

  if (checkinStatus === "DONE" && checkoutStatus === "NOT_DONE") {
    return GuestStatus.IN_HOUSE;
  }

  return null;
}

export function matchesGuestStatusFilter(
  booking: GuestStatusInput | null | undefined,
  filterStatus: string,
): boolean {
  if (!booking) {
    return false;
  }

  if (filterStatus === CHECKED_OUT_GUEST_STATUS) {
    return booking.checkoutStatus === "DONE";
  }

  return determineGuestStatus(booking) === filterStatus;
}
